import { useState } from 'react';
import { Drawer, TextField, MenuItem } from '@material-ui/core';
import { IconCoin, IconBuildingBank, IconBrandWhatsapp, IconAlertCircle } from '@tabler/icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useResponsive, useNotification } from '#root/hooks';
import { REFUND_PROCESSING_DAYS, BANK_ACCOUNT_MIN_LENGTH, BANK_ACCOUNT_HOLDER_MIN_LENGTH } from '#root/constants/business';
import { userService } from '#root/services';
import { changeCurrencySymbol } from '#root/utils';
import DrawerAppbar from '#root/components/DrawerAppbar';
import { Button } from '#root/components/ui';
import styles from './refundDrawer.module.scss';

interface RefundOptions {
  banks?: string[];
  currencies?: string[];
  processing_time?: {
    min_days: number;
    max_days: number;
  };
}

interface RefundDrawerProps {
  open: boolean;
  onClose: () => void;
  refundInProcess: boolean;
  refundOptions: RefundOptions;
  amount: number;
}

const validationSchema = Yup.object().shape({
  bank: Yup.string().required('Campo requerido'),
  currency: Yup.string().required('Campo requerido'),
  bank_account_number: Yup.string().min(BANK_ACCOUNT_MIN_LENGTH, '¡Demasiado corto!').required('Campo requerido'),
  bank_account_holder: Yup.string().min(BANK_ACCOUNT_HOLDER_MIN_LENGTH, '¡Demasiado corto!').required('Campo requerido'),
});

const RefundDrawer = ({
  open,
  onClose,
  refundInProcess,
  refundOptions,
  amount,
}: RefundDrawerProps) => {
  const { isDesktop } = useResponsive();
  const { createMessage } = useNotification();
  const [submitting, setSubmitting] = useState(false);

  const processingTime = refundOptions?.processing_time
    ? `${refundOptions.processing_time.min_days}-${refundOptions.processing_time.max_days} días útiles`
    : `${REFUND_PROCESSING_DAYS.min}-${REFUND_PROCESSING_DAYS.max} días útiles`;

  const fetchPostData = async (data: {
    bank: string;
    currency: string;
    bank_account_number: string;
    bank_account_holder: string;
  }) => {
    setSubmitting(true);
    const body = {
      bank: data.bank,
      currency: data.currency,
      bank_account_number: data.bank_account_number.replace(/-/g, '').replace(/ /g, ''),
      bank_account_holder: data.bank_account_holder,
    };
    const res = await userService.requestRefund(body);
    setSubmitting(false);
    if (res.error) {
      createMessage.error(res.message);
      return;
    }
    createMessage.success(res.message);
    onClose();
  };

  const formik = useFormik({
    initialValues: {
      bank: '',
      currency: '',
      bank_account_number: '',
      bank_account_holder: '',
    },
    validationSchema,
    onSubmit: (values) => {
      fetchPostData(values);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const renderInProcessContent = () => (
    <div className={styles.main}>
      <div className={styles.infoBox}>
        <div className={styles.infoIcon}>
          <IconAlertCircle size={24} />
        </div>
        <div className={styles.infoContent}>
          <p className={styles.infoTitle}>Tienes un reembolso en proceso</p>
          <p className={styles.infoDesc}>
            Si ha pasado más tiempo o quieres corregir la cuenta, contáctanos.
          </p>
        </div>
      </div>
      <div className={styles.processingTime}>
        <span className={styles.processingLabel}>Plazo de procesamiento:</span>
        <span className={styles.processingValue}>{processingTime}</span>
      </div>
      <hr className={styles.divider} />
      <div className={styles.contactSection}>
        <p className={styles.contactText}>¿Necesitas ayuda? Escríbenos:</p>
        <Button
          variant="whatsapp"
          fullWidth
          icon={<IconBrandWhatsapp size={18} />}
          to="https://api.whatsapp.com/send?phone=51960684330&text=Hola!">
          +51 960684330
        </Button>
        <p className={styles.schedule}>Lunes a Viernes de 9am a 6pm.</p>
      </div>
    </div>
  );

  const renderFormContent = () => (
    <div className={styles.main}>
      <div className={styles.infoBox}>
        <div className={styles.infoIcon}>
          <IconCoin size={24} />
        </div>
        <div className={styles.infoContent}>
          <p className={styles.infoTitle}>
            El monto a reembolsar será {changeCurrencySymbol('DOLARES')} {amount}
          </p>
        </div>
      </div>
      <p className={styles.helperText}>
        Coloca una cuenta en la misma moneda en la que hiciste el pago.
      </p>
      <hr className={styles.divider} />
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.sectionHeader}>
          <IconBuildingBank size={18} />
          <span>Datos bancarios</span>
        </div>
        <div className={styles.formFields}>
          <TextField
            fullWidth
            variant="outlined"
            label="Banco"
            select
            size="small"
            id="bank"
            name="bank"
            value={formik.values.bank}
            onChange={formik.handleChange}
            error={Boolean(formik.touched.bank && formik.errors.bank)}
            helperText={formik.touched.bank && formik.errors.bank}>
            {refundOptions.banks?.map((bank, index) => (
              <MenuItem key={index} value={bank}>
                {bank}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            variant="outlined"
            label="Moneda"
            select
            size="small"
            id="currency"
            name="currency"
            value={formik.values.currency}
            onChange={formik.handleChange}
            error={Boolean(formik.touched.currency && formik.errors.currency)}
            helperText={formik.touched.currency && formik.errors.currency}>
            {refundOptions.currencies?.map((currency, index) => (
              <MenuItem key={index} value={currency}>
                {currency}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            variant="outlined"
            label="Número de cuenta"
            size="small"
            id="bank_account_number"
            name="bank_account_number"
            value={formik.values.bank_account_number}
            onChange={formik.handleChange}
            error={Boolean(formik.touched.bank_account_number && formik.errors.bank_account_number)}
            helperText={formik.touched.bank_account_number && formik.errors.bank_account_number}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Titular"
            size="small"
            id="bank_account_holder"
            name="bank_account_holder"
            value={formik.values.bank_account_holder}
            onChange={formik.handleChange}
            error={Boolean(formik.touched.bank_account_holder && formik.errors.bank_account_holder)}
            helperText={formik.touched.bank_account_holder && formik.errors.bank_account_holder}
          />
        </div>
        <p className={styles.processingNote}>Tiempo de procesamiento: {processingTime}</p>
        <div className={styles.footer}>
          <Button
            fullWidth
            variant="primary"
            type="submit"
            loading={submitting}
            disabled={submitting}>
            Confirmar reembolso
          </Button>
        </div>
      </form>
    </div>
  );

  return (
    <Drawer
      anchor={isDesktop ? 'right' : 'bottom'}
      open={open}
      onClose={handleClose}
      PaperProps={{
        className: isDesktop ? styles.drawerPaperDesktop : styles.drawerPaperMobile,
      }}>
      <DrawerAppbar
        isMobile={!isDesktop}
        title="Solicitar Reembolso"
        onClose={handleClose}
      />
      {refundInProcess ? renderInProcessContent() : renderFormContent()}
    </Drawer>
  );
};

export default RefundDrawer;
